import type { FC, ReactNode } from 'react';
import { useState } from 'react';

import {
  autoUpdate,
  flip,
  offset,
  type Placement,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react';
import cx from 'classnames';

interface HoverPopoverProps {
  trigger: ReactNode;
  children: ReactNode;
  placement?: Placement;
  triggerClassName?: string;
  popoverClassName?: string;
  popoverStyle?: React.CSSProperties;
  onPopoverContextMenu?: React.MouseEventHandler<HTMLDivElement>;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const HoverPopover: FC<HoverPopoverProps> = ({
  trigger,
  children,
  placement = 'top-start',
  triggerClassName,
  popoverClassName,
  popoverStyle,
  onPopoverContextMenu,
  isOpen: controlledIsOpen,
  onOpenChange,
}) => {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);
  const isOpen = controlledIsOpen ?? uncontrolledIsOpen;
  const setIsOpen = onOpenChange ?? setUncontrolledIsOpen;

  const { refs, floatingStyles, context } = useFloating({
    placement,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    delay: { close: 150 },
    handleClose: safePolygon(),
  });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss]);

  return (
    <>
      <div ref={refs.setReference} className={triggerClassName} {...getReferenceProps()}>
        {trigger}
      </div>
      <div
        ref={refs.setFloating}
        role="presentation"
        style={{ ...floatingStyles, ...popoverStyle }}
        onContextMenu={onPopoverContextMenu}
        className={cx(
          'transition-all duration-150 origin-bottom',
          {
            'opacity-100 scale-100': isOpen,
            'opacity-0 scale-95 pointer-events-none': !isOpen,
          },
          popoverClassName,
        )}
        {...getFloatingProps()}
      >
        {children}
      </div>
    </>
  );
};
